import { useState, useEffect, useCallback } from 'react';

const CACHE_KEY_PREFIX = 'aiRecording:schoolLogo:';

interface SchoolInfo {
  SchoolLogo: string | null;
  School: string;
  ContactEmail?: string;
  SchoolAddress?: string;
  ContactPhone?: string;
}

interface UseSchoolLogoReturn {
  logoUrl: string | null;
  schoolName: string | null;
  loading: boolean;
  error: string | null;
}

interface CachedSchoolLogo {
  logoUrl: string | null;
  schoolName: string | null;
}

/**
 * Limpia la caché de logo de escuela (localStorage).
 * Debe llamarse al cerrar sesión o cuando expire el token para que el logo se vuelva a cargar en la siguiente sesión.
 */
export function clearSchoolLogoCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_KEY_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    if (keysToRemove.length > 0) {
      console.log('[useSchoolLogo] Cache cleared for session/token end:', keysToRemove.length, 'entries');
    }
  } catch (e) {
    console.warn('[useSchoolLogo] Error clearing logo cache:', e);
  }
}

function getCachedLogo(slug: string): CachedSchoolLogo | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + slug);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSchoolLogo;
    if (parsed && (typeof parsed.logoUrl === 'string' || parsed.logoUrl === null) && (typeof parsed.schoolName === 'string' || parsed.schoolName === null)) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function setCachedLogo(slug: string, data: CachedSchoolLogo): void {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + slug, JSON.stringify(data));
  } catch (e) {
    console.warn('[useSchoolLogo] Error saving logo to cache:', e);
  }
}

function getSasToken(): string {
  // Priority 1: clave directa (persistida por main.tsx al recibir el postMessage de ACV)
  try {
    const direct = (localStorage.getItem('SASToken') || '').trim();
    if (direct) return direct;
  } catch { /* ignore */ }
  // Priority 2: dentro del payload completo de msg
  try {
    const msg = JSON.parse(localStorage.getItem('msg') || '{}') as { user?: { SASToken?: string } };
    return (msg?.user?.SASToken || '').trim();
  } catch {
    return '';
  }
}

function buildLogoUrl(rawLogo: string, sasToken: string): string {
  if (!rawLogo) return '';
  const base = rawLogo.trim();
  if (!sasToken) return base;
  // Evitar duplicar parámetros SAS si ya están en la URL
  if (base.includes('sig=') || base.includes('se=')) return base;
  return base.includes('?') ? `${base}&${sasToken.replace(/^\?/, '')}` : `${base}${sasToken.startsWith('?') ? sasToken : `?${sasToken}`}`;
}

function getApiBaseUrl(): string {
  if (import.meta.env?.VITE_ACV_BACKEND_API) {
    const apiUrl = (import.meta.env.VITE_ACV_BACKEND_API as string).trim();
    if (apiUrl) return apiUrl;
  }
  if (typeof window !== 'undefined' && (window as any).ACV_API_URL) {
    return (window as any).ACV_API_URL;
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('ngrok') || hostname.includes('ngrok-free.app')) return 'https://acvnns-api-produc.acv.app';
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8000';
  }
  return 'https://acvnns-api-produc.acv.app';
}

/**
 * Hook para obtener el logo de la escuela desde el backend.
 * La primera vez que se carga para un slug se guarda en localStorage; en visitas posteriores se usa la caché
 * hasta que se cierre sesión o se invoque clearSchoolLogoCache().
 * Se recarga automáticamente cuando ACV App envía el SASToken vía postMessage.
 */
export function useSchoolLogo(): UseSchoolLogoReturn {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchoolLogo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams(window.location.search);
      const slug = params.get('slug') || localStorage.getItem('slug');

      if (!slug) {
        console.warn('[useSchoolLogo] No slug found, skipping logo fetch');
        setLoading(false);
        return;
      }

      // Use cache if SAS token is already available — avoids repeated API calls
      const cached = getCachedLogo(slug);
      const sasToken = getSasToken();
      if (cached && sasToken) {
        // Rebuild logo URL with the current (possibly refreshed) SAS token
        const logo = cached.logoUrl ? buildLogoUrl(cached.logoUrl.split('?')[0], sasToken) : null;
        setLogoUrl(logo);
        setSchoolName(cached.schoolName);
        setLoading(false);
        return;
      }

      const apiBase = getApiBaseUrl();
      const response = await fetch(`${apiBase}/api/school/get-school-info?slug=${slug}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch school info: ${response.status}`);
      }

      const result = await response.json() as { status?: string; result?: SchoolInfo[] };

      if (result.status === 'Success' && result.result && result.result.length > 0) {
        const schoolInfo = result.result[0];
        const freshSas = getSasToken();
        const logo = schoolInfo.SchoolLogo ? buildLogoUrl(schoolInfo.SchoolLogo, freshSas) : null;

        setLogoUrl(logo);
        setSchoolName(schoolInfo.School || null);
        // Cache the raw blob path (without SAS) so it can be re-signed on refresh
        setCachedLogo(slug, { logoUrl: schoolInfo.SchoolLogo || null, schoolName: schoolInfo.School || null });
      } else {
        setLogoUrl(null);
        setSchoolName(null);
        setCachedLogo(slug, { logoUrl: null, schoolName: null });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useSchoolLogo] Error fetching school logo:', errorMessage);
      setError(errorMessage);
      setLogoUrl(null);
      setSchoolName(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSchoolLogo();
  }, [fetchSchoolLogo]);

  // Re-fetch when ACV App delivers the SAS token via postMessage (may arrive after first render)
  useEffect(() => {
    const onSasReceived = () => void fetchSchoolLogo();
    window.addEventListener('airecording-refresh-school-logo', onSasReceived);
    return () => window.removeEventListener('airecording-refresh-school-logo', onSasReceived);
  }, [fetchSchoolLogo]);

  return { logoUrl, schoolName, loading, error };
}

